/**
 * Bulk Import Shareholders Modal
 *
 * Modal for importing multiple shareholders from a CSV file.
 */

import { useState, useRef } from "react";
import {
  Upload,
  Download,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/common/Modal";
import { Button } from "@/components/ui/button";
import { useBulkImportShareholders, useGetImportTemplate } from "@/hooks/use-share-book";
import type { BulkImportResult } from "@/lib/api";

interface BulkImportShareholdersModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
}

export function BulkImportShareholdersModal({
  isOpen,
  onClose,
  companyId,
}: BulkImportShareholdersModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [csvData, setCsvData] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [result, setResult] = useState<BulkImportResult | null>(null);

  const bulkImport = useBulkImportShareholders();
  const { data: template } = useGetImportTemplate(companyId);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".csv") && !file.type.includes("csv")) {
      toast.error("Vänligen välj en CSV-fil");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setCsvData(content);
      setFileName(file.name);
      setResult(null);
    };
    reader.readAsText(file);
  };

  const handleDownloadTemplate = () => {
    if (!template) return;

    const blob = new Blob([template], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "aktieagare-mall.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const handleImport = async () => {
    if (!csvData) {
      toast.error("Vänligen välj en CSV-fil först");
      return;
    }

    try {
      const importResult = await bulkImport.mutateAsync({
        companyId,
        csvData,
      });

      if (!importResult) {
        toast.error("Import misslyckades - inget resultat");
        return;
      }

      setResult(importResult);

      if (importResult.created > 0) {
        toast.success(`${importResult.created} aktieägare importerade`);
      }

      if (importResult.errors.length === 0 && importResult.skipped === 0) {
        // All successful, close after a short delay
        setTimeout(() => {
          handleClose();
        }, 2000);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Import misslyckades");
    }
  };

  const handleClose = () => {
    setCsvData("");
    setFileName("");
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  // Preview first few rows of CSV
  const getPreview = () => {
    if (!csvData) return null;

    const lines = csvData.trim().split("\n").slice(0, 6);
    return lines.map((line, i) => (
      <div
        key={i}
        className={`text-xs font-mono py-1.5 px-3 ${
          i === 0 ? "font-semibold bg-gray-100 text-gray-900" : "text-gray-600"
        }`}
      >
        {line.slice(0, 100)}
        {line.length > 100 && "..."}
      </div>
    ));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Importera aktieägare"
      size="lg"
    >
      <div className="space-y-6">
        {/* Instructions */}
        <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-blue-900 mb-1">CSV-format</p>
            <p className="text-blue-700">
              Filen ska innehålla kolumnerna: type, name, personalId, orgNumber, address, postalCode, city, country, email, phone
            </p>
            <p className="mt-2 text-blue-700">
              Ladda ner mallen nedan för ett exempel på korrekt format.
            </p>
          </div>
        </div>

        {/* Download template */}
        <div className="flex items-center gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleDownloadTemplate}
            disabled={!template}
          >
            <Download className="h-4 w-4 mr-2" />
            Ladda ner CSV-mall
          </Button>
        </div>

        {/* File upload */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Välj CSV-fil
          </label>
          <div className="flex items-center gap-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileSelect}
              className="hidden"
              id="csv-file-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              Välj fil
            </Button>
            {fileName && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <FileSpreadsheet className="h-4 w-4 text-gray-400" />
                <span>{fileName}</span>
              </div>
            )}
          </div>
        </div>

        {/* Preview */}
        {csvData && !result && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Förhandsgranskning
            </label>
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white max-h-40 overflow-y-auto">
              {getPreview()}
              {csvData.trim().split("\n").length > 6 && (
                <div className="text-xs text-gray-500 py-1.5 px-3 italic bg-gray-50">
                  ... och {csvData.trim().split("\n").length - 6} fler rader
                </div>
              )}
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">Skapade</span>
                </div>
                <div className="text-2xl font-bold text-green-800 mt-1">
                  {result.created}
                </div>
              </div>

              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 text-amber-700">
                  <Info className="h-5 w-5" />
                  <span className="font-medium">Hoppade över</span>
                </div>
                <div className="text-2xl font-bold text-amber-800 mt-1">
                  {result.skipped}
                </div>
              </div>

              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="h-5 w-5" />
                  <span className="font-medium">Fel</span>
                </div>
                <div className="text-2xl font-bold text-red-800 mt-1">
                  {result.errors.length}
                </div>
              </div>
            </div>

            {/* Error details */}
            {result.errors.length > 0 && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Fel som uppstod
                </label>
                <div className="border border-gray-200 rounded-lg overflow-hidden max-h-40 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">
                          Rad
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">
                          Namn
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">
                          Fel
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {result.errors.map((error, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 text-gray-600">
                            {error.row}
                          </td>
                          <td className="px-3 py-2 text-gray-600">
                            {error.name}
                          </td>
                          <td className="px-3 py-2 text-red-600">
                            {error.error}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button type="button" variant="outline" onClick={handleClose}>
            {result ? "Stäng" : "Avbryt"}
          </Button>
          {!result && (
            <Button
              type="button"
              onClick={handleImport}
              disabled={!csvData || bulkImport.isPending}
            >
              {bulkImport.isPending ? "Importerar..." : "Importera"}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
